import type { BeneficiaryDesignationInput } from "../../mutations";
import type { BeneficiaryRecord } from "../../../person/queries";

export const MASKED_SSN_LAST4 = "••••";

export const BENEFICIARY_APPLICATION_TYPES = ["member", "associate", "affiliate"] as const;

export type BeneficiaryApplicationType = (typeof BENEFICIARY_APPLICATION_TYPES)[number];

export const isBeneficiaryApplicationType = (value: string): value is BeneficiaryApplicationType =>
  BENEFICIARY_APPLICATION_TYPES.includes(value as BeneficiaryApplicationType);

export type BeneficiaryFormRecord = {
  recordId?: string;
  fullName: string;
  dateOfBirth: string;
  ssnLast4: string;
  relationship: string;
  percent: string;
  applicationType?: string;
};

export type BeneficiariesFormValues = {
  primaryBeneficiaries: BeneficiaryFormRecord[];
  contingentBeneficiaries: BeneficiaryFormRecord[];
};

export type BeneficiaryGroup = "primaryBeneficiaries" | "contingentBeneficiaries";

export const RELATIONSHIP_OPTIONS = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Domestic Partner",
  "Trust",
  "Estate",
  "Other"
].map((value) => ({ value, label: value }));

export const emptyBeneficiary = (applicationType?: string): BeneficiaryFormRecord => ({
  fullName: "",
  dateOfBirth: "",
  ssnLast4: "",
  relationship: "",
  percent: "",
  applicationType
});

export function blankBeneficiariesFormValues(applicationType?: string): BeneficiariesFormValues {
  return {
    primaryBeneficiaries: [emptyBeneficiary(applicationType)],
    contingentBeneficiaries: []
  };
}

export function applicationTypeFromProgramName(
  programTypeName?: string | null
): Extract<BeneficiaryApplicationType, "associate" | "member"> | null {
  const normalized = programTypeName?.trim().toLowerCase();
  if (normalized === "associate" || normalized === "member") return normalized;
  return null;
}

export function updateBeneficiaryApplicationTypes(
  programTypeName: string | null | undefined,
  activeRecords: Array<Pick<BeneficiaryRecord, "applicationType">>
): BeneficiaryApplicationType[] {
  const types = new Set<BeneficiaryApplicationType>();
  const fromProgram = applicationTypeFromProgramName(programTypeName);
  if (fromProgram) types.add(fromProgram);

  for (const record of activeRecords) {
    if (isBeneficiaryApplicationType(record.applicationType)) {
      types.add(record.applicationType);
    }
  }

  return [...types].sort();
}

export function toFormRecord(record: BeneficiaryRecord): BeneficiaryFormRecord {
  return {
    recordId: record.id,
    fullName: record.fullName,
    dateOfBirth: record.dateOfBirth,
    ssnLast4: record.ssnLast4,
    relationship: record.relationship,
    percent: String(record.percent),
    applicationType: record.applicationType
  };
}

export function buildDefaultValues(
  records: BeneficiaryRecord[],
  applicationTypeOverride?: string
): BeneficiariesFormValues {
  const applicationType = records[0]?.applicationType ?? applicationTypeOverride ?? "member";
  const primaryBeneficiaries = records.filter((record) => record.beneficiaryType === "primary").map(toFormRecord);
  const contingentBeneficiaries = records.filter((record) => record.beneficiaryType === "contingent").map(toFormRecord);

  return {
    primaryBeneficiaries: primaryBeneficiaries.length ? primaryBeneficiaries : [emptyBeneficiary(applicationType)],
    contingentBeneficiaries
  };
}

export function sumPercents(beneficiaries: BeneficiaryFormRecord[]): number {
  return beneficiaries.reduce((sum, beneficiary) => sum + Number(beneficiary.percent || 0), 0);
}

export function ssnLast4Validation(value: string, record?: BeneficiaryFormRecord) {
  if (record?.recordId && value === MASKED_SSN_LAST4) return true;
  return /^\d{4}$/.test(value) || "Must be exactly 4 digits";
}

export function requiredText(value: string | undefined): boolean {
  return (value ?? "").trim().length > 0;
}

export function isFilledBeneficiary(record: BeneficiaryFormRecord): boolean {
  const percent = Number(record.percent);
  return (
    requiredText(record.fullName) &&
    requiredText(record.dateOfBirth) &&
    requiredText(record.relationship) &&
    ssnLast4Validation(record.ssnLast4, record) === true &&
    Number.isInteger(percent) &&
    percent >= 1 &&
    percent <= 100
  );
}

export function beneficiariesGroupCanSave(records: BeneficiaryFormRecord[], required: boolean): boolean {
  if (records.length === 0) return !required;
  return records.every(isFilledBeneficiary) && sumPercents(records) === 100;
}

export function beneficiariesFormCanSave(values: BeneficiariesFormValues): boolean {
  return (
    beneficiariesGroupCanSave(values.primaryBeneficiaries, true) &&
    beneficiariesGroupCanSave(values.contingentBeneficiaries, false)
  );
}

export function applicationTypeLabel(applicationType: string): string {
  return applicationType.charAt(0).toUpperCase() + applicationType.slice(1);
}

export function toMutationInput(beneficiary: BeneficiaryFormRecord): BeneficiaryDesignationInput {
  return {
    ...(beneficiary.recordId ? { id: beneficiary.recordId } : {}),
    fullName: beneficiary.fullName.trim(),
    dateOfBirth: beneficiary.dateOfBirth,
    ...(beneficiary.ssnLast4 && beneficiary.ssnLast4 !== MASKED_SSN_LAST4 ? { ssnLast4: beneficiary.ssnLast4 } : {}),
    relationship: beneficiary.relationship,
    percent: Number(beneficiary.percent),
    applicationType: beneficiary.applicationType
  };
}

export function allocationSummaryHeadline(allocationSum: number, allocationValid: boolean): string {
  if (allocationValid) {
    return `Allocation currently ${allocationSum}%.`;
  }
  if (allocationSum > 100) {
    return `Allocation currently ${allocationSum}%. Total exceeds 100% by ${allocationSum - 100}%.`;
  }
  return `Allocation currently ${allocationSum}%. You have ${100 - allocationSum}% left to distribute.`;
}
